import { Button, Link, TextField, Typography } from '@material-ui/core';
import Auth from '@aws-amplify/auth';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import * as Yup from 'yup';
import { Done, Layout, useStyles } from './Layout';

const ResendConfirmation: React.FC<{
  email?: string;
}> = props => {
  const classes = useStyles();
  const [confirmationRecipient, setRecipient] = useState<null | string>(null);

  if (confirmationRecipient) {
    return (
      <Layout title="Resend a confirmation code">
        <Done
          redirect={{
            pathname: '/signup/confirm',
            search: `?email=${confirmationRecipient}`,
          }}
        >
          <Typography align="justify" variant="body2">
            A confirmation code was sent to your email address.
            <br />
            This code is valid for 24 hours.
            <br />
            <Link
              to={{
                pathname: '/signup/confirm',
                search: `?email=${confirmationRecipient}`,
              }}
              component={RouterLink}
            >
              Please visit the confirmation page after having received the
              confirmation code.
            </Link>
          </Typography>
        </Done>
      </Layout>
    );
  }

  return (
    <Layout title="Resend a confirmation code">
      <Formik
        initialValues={{ email: props.email || '' }}
        initialStatus={null as null | Error}
        onSubmit={async (
          values,
          { setSubmitting, setFieldError, setStatus },
        ) => {
          setSubmitting(true);
          setStatus(null);
          try {
            await Auth.resendSignUp(values.email.toLowerCase());
            setSubmitting(false);
            setRecipient(values.email.toLowerCase());
          } catch (err) {
            if (err.code === 'UserNotFoundException') {
              setFieldError('email', 'User not found');
            } else if (err.code === 'UsernameExistsException') {
              setFieldError('email', err.message);
            } else if (err.code === 'CodeMismatchException') {
              setFieldError('code', 'Invalid verification code provided');
            } else if (err.code === 'NotAuthorizedException') {
              setStatus(new Error('User is already confirmed'));
            } else {
              // Unhandled error, show it raw to the user
              setStatus(err);
            }

            setSubmitting(false);
          }
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email('A valid email is required')
            .required('Required'),
        })}
      >
        {formikProps => {
          const {
            handleSubmit,
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
            status,
          } = formikProps;

          return (
            <form className={classes.form} noValidate onSubmit={handleSubmit}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                disabled={!!props.email}
                autoComplete="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                error={!!status || !!(touched.email && errors.email)}
                helperText={touched.email && errors.email}
              />
              {status && (
                <Typography className={classes.globalError} variant="body2">
                  {status.message}
                </Typography>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                className={classes.submitButton}
              >
                Resend confirmation code
              </Button>
            </form>
          );
        }}
      </Formik>
    </Layout>
  );
};

export default ResendConfirmation;
